/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Pure route + progress helpers for the Walk Me Home tracker (WMH 3/4). Kept
// framework-free so the polyline decode and the "% of journey complete" math
// are unit-testable and shared by the walker map + the watcher tracking page.
//
// The route polyline is fetched ONCE from /api/directions at session start
// (encoded, Google's algorithm) and cached; these helpers decode it and, given
// the walker's live position, compute how far along that fixed route they are.

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Decode a Google "encoded polyline algorithm" string into [lat,lng] points.
 * Pure port of the standard algorithm (precision 1e-5). Returns [] for empty
 * or malformed input rather than throwing.
 */
export function decodePolyline(encoded: string | null | undefined): LatLng[] {
  if (!encoded) return [];
  const points: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  const len = encoded.length;

  while (index < len) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      if (index >= len) return points; // truncated → stop cleanly
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    const dLat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dLat;

    result = 0;
    shift = 0;
    do {
      if (index >= len) return points;
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    const dLng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dLng;

    points.push({ lat: lat * 1e-5, lng: lng * 1e-5 });
  }
  return points;
}

/**
 * Straight-line % of the trip REMAINING (100 → 0), distance-based. Used on the
 * walker's own card, which has its live position, the origin, and the
 * destination but not the decoded route polyline. 100 at the origin, 0 at the
 * destination; only moves as the walker physically moves (a parked walker's
 * value holds). Returns null if origin/destination coincide (no baseline).
 */
export function straightLinePercentRemaining(
  current: LatLng | null | undefined,
  origin: LatLng | null | undefined,
  destination: LatLng | null | undefined,
): number | null {
  if (!current || !origin || !destination) return null;
  const baseline = haversineMeters(origin, destination);
  if (baseline <= 0) return null;
  const remaining = haversineMeters(current, destination);
  const pct = Math.round((remaining / baseline) * 100);
  return Math.max(0, Math.min(100, pct));
}

const R_METERS = 6_371_000;

/** Great-circle distance between two points, metres. */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a.lat)) *
      Math.cos(toRad(b.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return 2 * R_METERS * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Total length of a polyline, metres. */
export function routeLengthMeters(route: LatLng[]): number {
  let total = 0;
  for (let i = 1; i < route.length; i++) {
    total += haversineMeters(route[i - 1], route[i]);
  }
  return total;
}

export interface RouteProgress {
  /** 0..1 fraction of the route length covered so far. */
  fraction: number;
  /** Whole-number percent (0..100), rounded. */
  percent: number;
  /** Metres still to travel along the route from the walker's projected point. */
  remainingMeters: number;
}

/**
 * How far along the fixed route the walker is. Finds the closest vertex on the
 * route to the walker's current position and measures the along-route distance
 * up to it. This is intentionally simple (nearest-vertex, not full segment
 * projection) — the route is dense enough that vertex granularity is fine for a
 * progress bar, and it can't run backwards past the start or beyond the end.
 *
 * Returns 0% for a <2-point route (nothing to measure). Clamps to [0,1].
 */
export function computeRouteProgress(
  route: LatLng[],
  current: LatLng,
): RouteProgress {
  if (route.length < 2) {
    return { fraction: 0, percent: 0, remainingMeters: 0 };
  }
  // Cumulative along-route distance at each vertex.
  const cum: number[] = [0];
  for (let i = 1; i < route.length; i++) {
    cum[i] = cum[i - 1] + haversineMeters(route[i - 1], route[i]);
  }
  const total = cum[cum.length - 1];
  if (total <= 0) return { fraction: 0, percent: 0, remainingMeters: 0 };

  // Nearest vertex to the walker.
  let nearestIdx = 0;
  let nearestDist = Infinity;
  for (let i = 0; i < route.length; i++) {
    const d = haversineMeters(route[i], current);
    if (d < nearestDist) {
      nearestDist = d;
      nearestIdx = i;
    }
  }
  const covered = cum[nearestIdx];
  const fraction = Math.max(0, Math.min(1, covered / total));
  return {
    fraction,
    percent: Math.round(fraction * 100),
    remainingMeters: Math.max(0, total - covered),
  };
}
